import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API, apiError } from "@/lib/api";
import { openSecure, secureClick } from "@/lib/openFile";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Trash2, Plus, Loader2, Users, CheckCircle2, ScrollText, Eye } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("it-IT"); } catch { return ""; } };

export const docViewUrl = (id, download = false) => `${API}/documenti/${id}/view${download ? "?download=true" : ""}`;

export function openDocumento(id) {
  openSecure(docViewUrl(id));
}

export default function Documenti() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [letture, setLetture] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ titolo: "", descrizione: "", categoria: "associativi", filename: "", content_type: "", file_data: "" });

  const { data: associativi = [] } = useQuery({ queryKey: ["documenti", "associativi"], queryFn: async () => (await api.get("/documenti?categoria=associativi")).data });
  const { data: regolamento = [] } = useQuery({ queryKey: ["documenti", "regolamento"], queryFn: async () => (await api.get("/documenti?categoria=regolamento")).data });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File troppo grande (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, filename: f.name, content_type: f.type, file_data: reader.result, titolo: s.titolo || f.name.replace(/\.[^.]+$/, "") }));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.titolo.trim() || !form.file_data) { toast.error("Titolo e file sono obbligatori"); return; }
    setSaving(true);
    try {
      await api.post("/documenti", form);
      toast.success("Documento caricato");
      qc.invalidateQueries({ queryKey: ["documenti"] });
      setOpen(false);
      setForm({ titolo: "", descrizione: "", categoria: "associativi", filename: "", content_type: "", file_data: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documenti/${id}`); qc.invalidateQueries({ queryKey: ["documenti"] }); toast.success("Documento eliminato"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const showLetture = async (doc) => {
    try { const { data } = await api.get(`/documenti/${doc.id}/letture`); setLetture({ doc, rows: data }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const DocRow = ({ d, ack }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-0" data-testid={`doc-${d.id}`}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-[#166534]"><FileText className="h-4 w-4" /></div>
        <div className="min-w-0">
          <a href={docViewUrl(d.id)} onClick={secureClick(docViewUrl(d.id))} target="_blank" rel="noopener noreferrer" data-testid={`doc-title-${d.id}`} className="block truncate font-semibold text-slate-900 hover:text-[#166534] hover:underline">{d.titolo}{ack && d.letto && <Badge variant="outline" className="ml-2 border-green-200 bg-green-50 text-green-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Letto</Badge>}</a>
          {d.descrizione && <p className="text-sm text-slate-500">{d.descrizione}</p>}
          <p className="text-xs text-slate-400">{d.filename} · {fmt(d.created_at)}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" asChild className="bg-[#14382C] hover:bg-[#0E281F]"><a href={docViewUrl(d.id)} onClick={secureClick(docViewUrl(d.id))} target="_blank" rel="noopener noreferrer" data-testid={`open-${d.id}`}><Eye className="mr-1.5 h-4 w-4" /> Apri</a></Button>
        <Button size="sm" variant="outline" asChild><a href={docViewUrl(d.id, true)} onClick={secureClick(docViewUrl(d.id, true), true)} data-testid={`download-${d.id}`}><Download className="mr-1.5 h-4 w-4" /> Scarica</a></Button>
        {isManager && <Button size="sm" variant="outline" data-testid={`letture-${d.id}`} onClick={() => showLetture(d)}><Users className="mr-1.5 h-4 w-4" /> Chi ha letto</Button>}
        {isManager && <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-doc-${d.id}`} onClick={() => remove(d.id)}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    </div>
  );

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Documenti</h1>
          <p className="mt-1 text-sm text-slate-500">Documenti associativi e regolamento, disponibili a tutti in sola lettura.</p>
        </div>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="new-doc-button"><Plus className="mr-2 h-4 w-4" /> Carica documento</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Carica documento</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Sezione</Label>
                  <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                    <SelectTrigger data-testid="doc-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="associativi">Documenti associativi</SelectItem>
                      <SelectItem value="regolamento">Regolamento e disposizioni operative (richiede presa visione)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Titolo</Label><Input data-testid="doc-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} /></div>
                <div className="space-y-2"><Label>Descrizione (opzionale)</Label><Textarea rows={2} data-testid="doc-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
                <div className="space-y-2"><Label>File (PDF, immagine, ... max 8MB)</Label><Input ref={fileRef} type="file" data-testid="doc-file" onChange={onFile} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={submit} disabled={saving} data-testid="save-doc-button" className="bg-[#14382C] hover:bg-[#0E281F]">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Carica</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="mb-6 border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <h2 className="mb-2 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><ScrollText className="h-5 w-5 text-[#D97706]" /> Regolamento e disposizioni operative</h2>
          {regolamento.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Nessun documento.</p> : regolamento.map((d) => <DocRow key={d.id} d={d} ack />)}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <h2 className="mb-2 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><FileText className="h-5 w-5 text-[#166534]" /> Documenti associativi</h2>
          {associativi.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Nessun documento.</p> : associativi.map((d) => <DocRow key={d.id} d={d} />)}
        </CardContent>
      </Card>

      <Dialog open={Boolean(letture)} onOpenChange={(v) => !v && setLetture(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto gz-scroll">
          <DialogHeader><DialogTitle>Letture · {letture?.doc?.titolo}</DialogTitle></DialogHeader>
          {letture?.rows?.length ? (
            <div className="divide-y divide-slate-100">
              {letture.rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid={`lettura-${i}`}>
                  <div><p className="font-semibold text-slate-900">{r.user_name || r.user_email}</p><p className="text-xs text-slate-500">{r.user_email}</p></div>
                  <span className="text-xs text-slate-500">{fmt(r.letto_at)}</span>
                </div>
              ))}
            </div>
          ) : <p className="py-6 text-center text-sm text-slate-400">Nessuno ha ancora letto questo documento.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
